import { useState } from "react";
import { Alert, Autocomplete, Button, Card, Grid, Stack, Table, Text } from "@mantine/core";
import { IconArrowRight, IconWand } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { RecipeView } from "../components/RecipeView";
import { GOAL_OPTIONS, KitchenPending, NutritionChips, PreferencesFields } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { buildPreferences, hasNutrition, kitchenRequest, type PreferencesForm } from "../lib/kitchen";

const SAMPLE_RECIPE = `Classic mac and cheese (serves 4)
- 450 g elbow macaroni
- 4 tbsp butter
- 1/4 cup all-purpose flour
- 3 cups whole milk
- 1 cup heavy cream
- 4 cups shredded sharp cheddar
- 1 tsp salt, 1/2 tsp pepper
- 1 cup panko mixed with 2 tbsp melted butter

Boil the pasta. Melt butter, whisk in flour, then milk and cream; simmer until thick. Stir in cheese, salt and pepper, then the pasta. Top with buttered panko and bake at 190°C for 20 minutes.`;

export function RecipeMakeoverPage() {
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [goal, setGoal] = useState("high protein");
  const [prefs, setPrefs] = useState<PreferencesForm>({});
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.recipes.makeover(req) });

  const submit = () => {
    if (!input.text.trim() && !input.media) return;
    const req = kitchenRequest({ text: input.text, media: input.media, goal, message: composeMessage(note), preferences: buildPreferences(prefs) });
    setLast(req);
    m.mutate(req);
  };
  const data = m.data?.data;
  const after = data?.recipe?.linkedNutrition?.perServing && hasNutrition(data.recipe.linkedNutrition.perServing) ? data.recipe.linkedNutrition.perServing : data?.recipe?.estimatedNutritionPerServing;

  return (
    <RequireFeature feature="cooking">
      <PageHeader
        eyebrow="POST /napi/tools/recipes/makeover"
        title="Recipe makeover"
        description="Paste, photograph, film or read out a recipe and pick a goal. Every swap comes back with its reason and per-serving effect, plus the rewritten recipe with linked ingredients."
        actions={<RouteModelPicker routes={["recipesMakeover"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <MediaInput value={input} onChange={setInput} textPlaceholder="Paste a recipe (ingredients and method)…" photoLabel="Drop a photo of the recipe card or page" />
              {!input.media && !input.text && (
                <Button variant="subtle" color="violet" size="xs" onClick={() => setInput({ ...input, text: SAMPLE_RECIPE })}>
                  Use a sample recipe (mac and cheese)
                </Button>
              )}
              <Autocomplete label="Goal" data={GOAL_OPTIONS} value={goal} onChange={setGoal} placeholder="e.g. high protein, lower sodium, vegan" />
              <PreferencesFields value={prefs} onChange={setPrefs} fields={["diet", "allergies", "dislikes", "conditions"]} />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. keep it kid friendly" />
              <Button leftSection={<IconWand size={16} />} onClick={submit} loading={m.isPending} disabled={!input.text.trim() && !input.media} size="md">
                Make it over
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && m.mutate(last)} />}
          {m.isPending && <KitchenPending title="Reworking the recipe…" />}
          {data && !m.isPending && (
            <Stack gap="md">
              <ResultHeader title={data.originalTitle ? `${data.originalTitle}, made over` : "Made over"} meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              <Card>
                <Stack gap="sm">
                  {data.summary && <Text>{data.summary}</Text>}
                  <Stack gap={4}>
                    <NutritionChips n={data.originalNutritionPerServing} size="xs" label="before, per serving (AI estimate)" />
                    <NutritionChips n={after} label={after === data.recipe?.estimatedNutritionPerServing ? "after, per serving (AI estimate)" : "after, per serving (linked Passio foods)"} />
                  </Stack>
                  {data.swaps.length > 0 && (
                    <Table verticalSpacing={6} fz="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Was</Table.Th>
                          <Table.Th w={24} />
                          <Table.Th>Now</Table.Th>
                          <Table.Th>Why</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {data.swaps.map((s, i) => (
                          <Table.Tr key={i}>
                            <Table.Td c="dimmed">{s.original}</Table.Td>
                            <Table.Td>
                              <IconArrowRight size={14} />
                            </Table.Td>
                            <Table.Td fw={600}>{s.replacement}</Table.Td>
                            <Table.Td>
                              <Text fz="sm">{s.reason}</Text>
                              {s.impact && (
                                <Text fz="xs" c="lime.9" ff="monospace">
                                  {s.impact}
                                </Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Card>
              {data.notes && (
                <Alert color="violet" variant="light">
                  <Text fz="sm">{data.notes}</Text>
                </Alert>
              )}
              {data.recipe ? <RecipeView recipe={data.recipe} /> : <EmptyState title="No recipe found">{data.notes || "Send the ingredients with amounts and the method."}</EmptyState>}
            </Stack>
          )}
          {!data && !m.isPending && !m.error && <EmptyState title="Bring a recipe">Ingredients with amounts give the best before-and-after numbers.</EmptyState>}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
